import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import LinearProgress from "@material-ui/core/LinearProgress";
import TextField from "@material-ui/core/TextField";
import AssessmentIcon from "@material-ui/icons/Assessment";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import green from "@material-ui/core/colors/green";
import { useHistory } from "../../../shared/hooks/useHistory";

import {
  asyncSendValidateCodeEmail,
  asyncValidateEmailCode,
  resetRedirectPath,
} from "../../actions/resend-welcome-email.actions";
import {
  resetRequest,
  setClose,
} from "../../../shared/request/actions/requestActions";
import { validateEmail } from "../../../shared/validateEmail";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  success: {
    color: green[500],
  },
  messageNote: {
    color: "#a3afb7",
    fontSize: theme.spacing(1.5),
    display: "flex",
    marginBottom: "10px",
  },
  verificationTitle: {
    marginTop: "30px",
  },
  clickHereText: {
    color: "#76838f",
    cursor: "pointer",
    fontWeight: "bold",
  },
}));

export const ConfirmValidationCode = ({ dispatch, email, handleCancel }) => {
  const classes = useStyles();
  const { request, redirectPath } = useSelector((state) => ({
    request: state.request.request,
    redirectPath: state.auth.recoverAssessmentAccess.redirectPath,
  }));
  const [textCode, setTextCode] = useState("");
  const [textCodeValidation, setTextCodeValidation] = useState({
    isError: false,
    message: "",
  });

  const handleChange = (e) => {
    setTextCode(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isFieldEmpty = !textCode.length;

    if (isFieldEmpty) {
      setTextCodeValidation({
        isError: true,
        message: "The code is required",
      });
      return;
    }

    setTextCodeValidation({ isError: false, message: "" });
    dispatch(asyncValidateEmailCode(email, textCode));
  };

  useEffect(() => {
    if (redirectPath !== "") {
      dispatch(resetRedirectPath());
      window.location.replace(redirectPath);
    }
  }, [dispatch, redirectPath]);

  return (
    <div className={classes.paper}>
      <Typography component="h1" variant="h5">
        Verification Code
      </Typography>
      <Typography
        component="h1"
        variant="body1"
        className={classes.verificationTitle}
      >
        Enter the verification code sent to your email
      </Typography>
      <form className={classes.form} noValidate onSubmit={handleSubmit}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="textCode"
          label="Text Code"
          name="textCode"
          autoComplete="textCode"
          autoFocus
          value={textCode}
          onChange={handleChange}
          error={textCodeValidation.isError}
          helperText={textCodeValidation.message}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          disabled={request.isFetching}
        >
          Verify Code
        </Button>
        <Button fullWidth onClick={handleCancel}>
          Cancel
        </Button>
      </form>
    </div>
  );
};

export const SendEmailForm = ({
  email,
  handleChange,
  onSubmit,
  handleCancel,
}) => {
  const classes = useStyles();
  const request = useSelector((state) => state.request.request);
  const [emailValid, setEmailValid] = useState({
    isError: false,
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEmailEmpty = !email.length;
    const isEmailInvalid = !validateEmail(email);

    if (isEmailEmpty || isEmailInvalid) {
      setEmailValid({
        isError: true,
        message: isEmailEmpty
          ? "The email is required"
          : "The email is not correct",
      });
      return;
    }

    setEmailValid({ isError: false, message: "" });
    onSubmit(email);
  };

  return (
    <div className={classes.paper}>
      <Avatar className={classes.avatar}>
        <AssessmentIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        Assessment Validation Email Send
      </Typography>

      <form className={classes.form} noValidate onSubmit={handleSubmit}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="username"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={handleChange}
          error={emailValid.isError}
          helperText={emailValid.message}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          disabled={request.isFetching}
        >
          Send Validation Email
        </Button>

        <Button fullWidth onClick={handleCancel}>
          Cancel
        </Button>
      </form>
    </div>
  );
};

export const SendAssessmentValidationEmailPage = () => {
  const history = useHistory();
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [backupEmail, setBackupEmail] = useState("");
  const [wasEmailSent, setWasEmailSent] = useState(false);
  const [wasFormSubmitted, setWasFormSubmitted] = useState(false);

  const dispatch = useDispatch();
  const request = useSelector((state) => state.request.request);

  useEffect(() => {
    dispatch(resetRequest());
  }, [dispatch]);

  useEffect(() => {
    if (wasFormSubmitted && request.type === "success") {
      setWasEmailSent(true);
    }
  }, [wasFormSubmitted, request]);

  const handleEmailSendSubmit = () => {
    setWasFormSubmitted(true);
    setBackupEmail(`${email}`);

    dispatch(asyncSendValidateCodeEmail(email));
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleClose = () => {
    dispatch(setClose());
  };

  const handleCancelButton = () => {
    dispatch(resetRequest());
    history.goBack();
  };

  const handleVerificationCodeCancel = () => {
    dispatch(resetRequest());
    setWasEmailSent(false);
  };

  return (
    <>
      {request.isFetching && (
        <div className={classes.root}>
          <LinearProgress color="secondary" />
        </div>
      )}
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        {!wasEmailSent && (
          <SendEmailForm
            email={email}
            handleChange={handleEmailChange}
            onSubmit={handleEmailSendSubmit}
            handleCancel={handleCancelButton}
          />
        )}

        {wasEmailSent && (
          <ConfirmValidationCode
            dispatch={dispatch}
            email={backupEmail}
            handleCancel={handleVerificationCodeCancel}
          />
        )}

        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={request.isOpen}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={request.type !== "success" ? "warning" : "success"}
          >
            {request.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

SendEmailForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
};

ConfirmValidationCode.propTypes = {
  dispatch: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  handleCancel: PropTypes.func.isRequired,
};
